import React, { useContext, useMemo, useRef, useState } from 'react';
import { Button, Col, Row } from 'tea-component';

import TopicTier from './TopicTier';
import KeywordAndFeeds from './KeywordAndFeeds';
import { FilterContext } from '../../../../context';
import useIntersectionObserver from '../../../../hooks/useIntersectionObserver';
import PercentageItem from '@/pages/opinion/components/Snippets/PercentageItem';
import RingRatioBubble from '@/pages/opinion/components/Snippets/RingRatioBubble';
import {formatSameDayFunc } from '@/pages/opinion/utils/time';
import { useTopicLevelTwoTimeline } from '../../../../hooks/useTimelineData';
import {
  reviewsFormat,
  specialSecimalFormatPercentage,
} from '@/pages/opinion/utils/value';
import './index.scss';
import SvgIcon from '@/pages/home/components/SvgIcon';
import CardCommon from '@/pages/opinion/components/CardCommon';
import { useTopicLevelTwoList } from '@op/pages/Summary/hooks/useTopicConfig';
import { LanguageType } from '@op/types/common';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import useLanguage from '@op/hooks/useLanguage';
/**
 *
 * @param title
 * @param childTopics  二级话题渲染的数据
 * @param topicTier2Select  用于过滤使用的
 * @param colSpan  给Col组件使用的，lineShowCount 是展示的个数
 * @returns
 */
export default function TopicItem({
  childTopics,
  colSpan,
  title,
  data,
  topic2Select,
  index,
  language,
}: {
  childTopics: Array<string>;
  colSpan: number;
  title: string;
  data: any;
  topic2Select: Array<string>;
  index: number;
  language: LanguageType;
}) {
  const { filter, transTopic } = useContext(FilterContext);
  const { formatMessage } = useLanguage();

  // 请求Topic列表数据
  const { total, loading, tableRecodeMap, tableData } = useTopicLevelTwoList({
    filter,
    topic: childTopics,
    index,
  });

  // 批量请求图表数据
  const {
    timelineMap,
    loading: chartLoading,
    apiError: chartApiError,
  } = useTopicLevelTwoTimeline({
    index,
    topic: childTopics,
    filter,
    topicLevel: 'L2',
  });

  const {
    setActiveElementHeight,
    showMore,
    noData,
    renderTopicList,
    lineShowCount,
    renderTotal,
    setActive,
    setPage,
    topicTierRef,
    page,
    active,
    show,
  } = useExcludeData({
    topic2Select,
    childTopics,
    colSpan,
    topicList: tableData,
    total,
  });

  const colElements = useMemo(() => {
    let keys: Array<string> = [];
    const elements: Array<JSX.Element> = [];
    renderTopicList.forEach(({ topic }, index) => {
      elements.push(
        <Col span={colSpan} key={title + topic}>
          <TopicTier
            loading={chartLoading}
            apiError={chartApiError}
            onClick={() => {
              setActive(topic === active ? '' : topic);
              buriedBtnPoint('STO_tier2TopicCard');
            }}
            id={topic}
            valueKey={'mentions'}
            show={show}
            filter={filter}
            tableData={tableRecodeMap[topic]}
            chartData={timelineMap.mentions[topic]}
            active={active === topic}
          ></TopicTier>
        </Col>,
      );
      keys.push(topic);
      const currentIndex = index + 1;
      if (
        currentIndex % lineShowCount === 0 ||
        (currentIndex === renderTopicList.length && keys.length > 0)
      ) {
        elements.push(
          <KeywordAndFeeds
            setActiveElementHeight={setActiveElementHeight}
            key={`${title + keys.join(',')}-detail`}
            id={active}
            showKeys={keys}
            language={language}
          ></KeywordAndFeeds>,
        );
        keys = [];
      }
    });
    return [...elements];
  }, [
    renderTopicList,
    active,
    chartLoading,
    chartApiError,
    lineShowCount,
    setActiveElementHeight,
    show,
    formatMessage,
    language,
  ]);

  return (
    <div id={title} className="dbo-topic-item-wrapper" ref={topicTierRef}>
      <CardCommon
        empty={noData}
        loading={loading}
        loadingHeight={275}
        contentCard
        title={
          <TopicLevelOneHeader
            data={data}
            title={title}
            transTopic={transTopic}
            filter={filter}
          ></TopicLevelOneHeader>
        }
      >
        <>
          <Row className="row-info-box">{colElements}</Row>

          <MoreBtn
            lineShowCount={lineShowCount}
            page={page}
            renderTotal={renderTotal}
            setPage={setPage}
            showMore={showMore}
          ></MoreBtn>
        </>
      </CardCommon>
    </div>
  );
}

function TopicLevelOneHeader({
  transTopic,
  title,
  data,
  filter,
}: {
  transTopic: any;
  title: string;
  data: any;
  filter: any;
}) {
  const {
    mentions,
    mentions_qoq,
    mentions_old,
    newTimes,
    oldTimes,
    mentions_ratio,
  } = data || {};
  const { formatMessage } = useLanguage();

  return (
    <div className="dbo-title-info">
      <span className="dbo-title-info-title">{transTopic(title, 'L1')}</span>
      <span className="dbo-title-info-desc">
        {reviewsFormat(mentions)}
        {` (${specialSecimalFormatPercentage(mentions_ratio)})`}
      </span>
      <RingRatioBubble
        formateValue={reviewsFormat}
        formateDate={formatSameDayFunc(filter)}
        oldTime={oldTimes}
        newTime={newTimes}
        oldValue={mentions_old}
        newValue={mentions}
        title={formatMessage({ id: 'Mentions' })}
      >
        <PercentageItem percentage={mentions_qoq}></PercentageItem>
      </RingRatioBubble>
    </div>
  );
}

function MoreBtn({
  lineShowCount,
  page,
  renderTotal,
  setPage,
  showMore,
}: {
  lineShowCount: number;
  page: number;
  renderTotal: number;
  setPage: (page: number) => void;
  showMore?: boolean;
}) {
  const { formatMessage } = useLanguage();

  const showMoreBtn = useMemo(
    () => renderTotal > page * lineShowCount,
    [page, lineShowCount, renderTotal],
  );

  if (!showMore) return null;

  return (
    <div className="bottom-btn-text">
      {page > 1 && (
        <Button onClick={() => setPage(1)} type="link">
          {formatMessage({ id: 'Close' })}
          <SvgIcon type="point-top" className="more-btn-icon" />
        </Button>
      )}
      {showMoreBtn && (
        <Button onClick={() => setPage(page + 2)} type="link">
          {formatMessage({ id: 'Show More' })}
          <SvgIcon type="point-down" className="more-btn-icon" />
        </Button>
      )}
    </div>
  );
}

/**
 * 除了请求数据的其他数据在这个hook里处理
 */
function useExcludeData({
  topic2Select,
  childTopics,
  colSpan,
  topicList,
  total,
}: {
  topic2Select: Array<string>;
  childTopics: Array<string>;
  colSpan: number;
  topicList: Array<any>;
  total: number;
}) {
  const topicTierRef = useRef<HTMLDivElement>(null);
  const [activeElementHeight, setActiveElementHeight] = useState(0);
  const [active, setActive] = useState<string>('');
  const [page, setPage] = useState(1);
  const { show } = useIntersectionObserver({
    target: topicTierRef.current,
  });

  const [renderTopicList, lineShowCount, renderTotal] = useMemo(() => {
    const selected = (topic2Select || []).map((item: string) =>
      item.toLocaleLowerCase(),
    );
    const filter = (childTopics || [])?.filter((item: any) =>
      selected.includes(item),
    );
    const renderList = filter.length === 0 ? childTopics : filter;
    const pageTotal = 24 / colSpan;
    const renderTopicList = topicList.filter((item) =>
      renderList.includes(item.topic),
    );

    return [
      renderTopicList.slice(0, pageTotal * page),
      pageTotal,
      renderTopicList.length,
    ];
  }, [topicList, topic2Select, page, colSpan]);

  const [showMore, noData] = useMemo(() => {
    const showMore = total > lineShowCount;
    return [showMore, total === 0];
  }, [page, activeElementHeight, active, show, total, lineShowCount]);

  return {
    setActiveElementHeight,
    showMore,
    noData,
    active,
    show,
    renderTopicList,
    lineShowCount,
    renderTotal,
    setActive,
    page,
    setPage,
    topicTierRef,
  };
}
